import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { WarningOctagon, CircleNotch } from "@phosphor-icons/react";
import { formatDate } from "@/lib/dates";

async function fetchGateInto(setItems) {
  try {
    const { data } = await api.get("/overdue-gate");
    setItems(data.items);
  } catch {
    setItems([]); // never lock the user out on an API failure
  }
}

function GateItem({ item, isAdmin, onResolved }) {
  const [mode, setMode] = useState(null); // null | update | lost
  const [newDate, setNewDate] = useState("");
  const [lossReason, setLossReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      onResolved(item.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveDate = () => run(async () => {
    if (!newDate) throw { response: { data: { detail: "Choose a new target close date first" } } };
    await api.patch(`/opportunities/${item.id}`, { target_close_date: newDate, version: item.version });
  }, "Target close date updated");

  const closeWon = () => run(async () => {
    const path = isAdmin ? "confirm-close-won" : "request-close-won";
    await api.post(`/opportunities/${item.id}/${path}`, { version: item.version });
  }, isAdmin ? "Opportunity closed as Won" : "Close-Won requested — an administrator will confirm");

  const closeLost = () => run(async () => {
    if (!lossReason.trim()) throw { response: { data: { detail: "A loss reason is required" } } };
    await api.post(`/opportunities/${item.id}/close-lost`, { loss_reason: lossReason.trim(), version: item.version });
  }, "Opportunity closed as Lost");

  const bypass = () => run(async () => {
    await api.post(`/opportunities/${item.id}/overdue-bypass`);
  }, "Bypassed once — the Global Administrator has been notified");

  return (
    <div className="rounded-md border border-[#E4E4E7] p-3" data-testid={`gate-item-${item.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 text-sm font-bold text-[#09090B]">
          {item.name}
          <span className="ml-1.5 font-normal text-[#6D6D70]">· {item.organization || "—"} · {item.stage_name || "—"}</span>
        </p>
        <p className="text-xs font-bold text-[#DC2626]">Target close {formatDate(item.target_close_date)} — overdue</p>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === "update" ? null : "update")}
          className="rounded-sm text-xs font-semibold" data-testid={`gate-update-${item.id}`}>
          Update close date
        </Button>
        <Button size="sm" disabled={busy} onClick={closeWon}
          className="rounded-sm bg-[#569A1F] text-xs font-semibold text-white hover:bg-[#457d18]"
          data-testid={`gate-close-won-${item.id}`}>
          Close Won
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setMode(mode === "lost" ? null : "lost")}
          className="rounded-sm border-[#DC2626]/40 text-xs font-semibold text-[#B91C1C] hover:bg-[#DC2626]/5"
          data-testid={`gate-close-lost-${item.id}`}>
          Close Lost
        </Button>
        {item.bypass_used ? (
          <span className="text-[11px] font-semibold text-[#B45309]" data-testid={`gate-no-bypass-${item.id}`}>
            Bypass already used — an update or close decision is required.
          </span>
        ) : (
          <Button size="sm" variant="ghost" disabled={busy} onClick={bypass}
            className="rounded-sm text-xs font-semibold text-[#6D6D70]" data-testid={`gate-bypass-${item.id}`}>
            Bypass once
          </Button>
        )}
        {busy && <CircleNotch size={16} className="animate-spin text-[#002248]" />}
      </div>
      {mode === "update" && (
        <div className="mt-2 flex items-center gap-2">
          <DatePicker value={newDate} onChange={setNewDate} className="w-44" testId={`gate-new-date-${item.id}`} />
          <Button size="sm" disabled={busy} onClick={saveDate}
            className="rounded-sm bg-[#002248] text-xs font-semibold text-white hover:bg-[#001731]"
            data-testid={`gate-save-date-${item.id}`}>
            Save new date
          </Button>
        </div>
      )}
      {mode === "lost" && (
        <div className="mt-2 flex items-center gap-2">
          <Input value={lossReason} onChange={(e) => setLossReason(e.target.value)}
            placeholder="Loss reason (required)" className="w-64 rounded-sm text-sm"
            data-testid={`gate-loss-reason-${item.id}`} />
          <Button size="sm" disabled={busy} onClick={closeLost}
            className="rounded-sm bg-[#DC2626] text-xs font-semibold text-white hover:bg-[#B91C1C]"
            data-testid={`gate-confirm-lost-${item.id}`}>
            Confirm Closed-Lost
          </Button>
        </div>
      )}
    </div>
  );
}

// Blocking login prompt (item 7): owners of open opportunities past their
// Target Close date must update, close, or bypass once before continuing.
export function OverdueGate() {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const helpers = useRef({ fetchGateInto });
  const load = useCallback(() => helpers.current.fetchGateInto(setItems), [helpers, setItems]);

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id, load]);

  const resolved = (id) => setItems((prev) => (prev || []).filter((i) => i.id !== id));

  if (!items || items.length === 0) return null;

  return (
    <Dialog open>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-lg sm:max-w-2xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="overdue-gate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#002248]">
            <WarningOctagon size={22} className="text-[#DC2626]" /> Overdue opportunities need a decision
          </DialogTitle>
          <DialogDescription>
            {items.length === 1 ? "One open opportunity is" : `${items.length} open opportunities are`} past
            the Target Close date. Update the date, record a close decision, or bypass once
            (the Global Administrator is notified — the next sign-in requires an action).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {items.map((i) => <GateItem key={i.id} item={i} isAdmin={isAdmin} onResolved={resolved} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
